import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shuffle, Loader2 } from "lucide-react";
import { fetchRandomDossier } from "../lib/api";
import { toast } from "sonner";

/**
 * VaultRouletteButton — slot-machine style CTA. Click → server picks a random
 * file from the whole archive (curated dossier, AI dossier, or connection)
 * and the user is teleported to it. Adds a satisfying spin animation while
 * the request is in flight.
 *
 * Used as a feature button on the homepage hero.
 */
export default function VaultRouletteButton({ variant = "hero" }) {
  const navigate = useNavigate();
  const [spinning, setSpinning] = useState(false);

  const spin = async () => {
    if (spinning) return;
    setSpinning(true);
    try {
      const d = await fetchRandomDossier();
      // The endpoint returns {type, slug_or_id, route} — try each shape.
      const route = d?.route
        || (d?.slug ? `/dossier/${d.slug}` : null)
        || (d?.id && d?.type === "deep_dive" ? `/ai-dossier/${d.id}` : null)
        || (d?.id && d?.type === "connection" ? `/connection/${d.id}` : null);
      if (!route) {
        toast.error("Couldn't roll — try again");
        return;
      }
      // Subtle delay so the spin animation gets to play at least one full loop
      setTimeout(() => navigate(route), 450);
    } catch {
      toast.error("Roulette jammed — try again");
    } finally {
      setTimeout(() => setSpinning(false), 500);
    }
  };

  // "grid" variant matches the uniform hero-CTA button look so the random
  // file shuffle sits as the final cell of the aligned hero grid.
  if (variant === "grid") {
    return (
      <button
        type="button"
        onClick={spin}
        disabled={spinning}
        data-testid="vault-roulette-btn"
        className="inline-flex items-center justify-center gap-2 bg-transparent text-[#ff9500] border border-[#ff9500]/50 hover:bg-[#ff9500] hover:text-[#050505] px-3 py-3 font-mono text-[10px] sm:text-xs uppercase tracking-[0.18em] transition-colors whitespace-nowrap disabled:opacity-70"
      >
        {spinning
          ? <Loader2 size={13} className="animate-spin" />
          : <Shuffle size={13} className="group-hover:rotate-180 transition-transform duration-500" />
        }
        {spinning ? "Rolling…" : "Vault Roulette"}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={spin}
      disabled={spinning}
      data-testid="vault-roulette-btn"
      className="group relative inline-flex items-center gap-3 px-6 py-3 border-2 border-[#ff9500] bg-[#ff9500]/5 text-[#ff9500] hover:bg-[#ff9500] hover:text-[#050505] font-mono text-[12px] tracking-[0.25em] uppercase transition-all duration-200 disabled:opacity-80"
    >
      {spinning ? (
        <Loader2 size={14} className="animate-spin" />
      ) : (
        <Shuffle size={14} className="group-hover:rotate-180 transition-transform duration-500" />
      )}
      <span className="font-bold">{spinning ? "Rolling…" : "Vault Roulette"}</span>
      <span className="font-mono text-[9px] tracking-[0.2em] opacity-70 hidden sm:inline">
        // RANDOM FILE
      </span>
    </button>
  );
}
